/* eslint-disable i18next/no-literal-string -- copy is wrapped in t(); disable kept for decorative literals (arrows, ✓). */
import {
  AddCircleIcon,
  ArrowLeftIcon,
  Avatar,
  Button,
  ButtonLink,
  DeleteIcon,
  EmailInput,
  Input,
  Select,
  Spinner,
  UploadIcon,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchEmailUsage } from '../../api/firebase/users';
import { getSourceGuide, SourceInstructions } from './StepSwitchGuide';
import { blankRow, fieldErrors, parseCsv, RosterRow, validateRow, WizardRoom } from './types';

// Local mirror of types.ts's email shape check, so we only fire the (async,
// platform-wide) email-usage lookup once a field holds a plausibly-complete
// address.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface Step2RosterProps {
  stepper?: ReactNode;
  roster: RosterRow[];
  sourceId?: string;
  importedFromFileName?: string;
  // The org's real rooms; the Room dropdown and CSV matching bind to these.
  rooms: WizardRoom[];
  // Room new rows default to (the org's first active room).
  defaultRoomId: string;
  onChange: (roster: RosterRow[]) => void;
  onImport: (rows: RosterRow[], fileName?: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

// Optional gender select. Values are the GenderEnum the student record uses;
// imported values are normalized to these in parseCsv (types.ts).
const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];

/**
 * Step 2, Add your student list. One card per student (fields wrap responsively
 * so nothing scrolls sideways), capturing the same required fields as the
 * single-student Add Student form: the child's first name, last name, and
 * birthday, plus a parent's first name, last name, and email, and the room.
 * Everyone is enrolled starting today.
 */
export function Step2Roster(p: Step2RosterProps) {
  const { t } = useTranslation();
  const { isMobileOrgSwitcherEnabled } = useFlags();
  const validations = useMemo(() => p.roster.map((r) => validateRow(r)), [p.roster]);
  const validCount = validations.filter((v) => !v.isBlank && v.errors.length === 0).length;
  // A started-but-incomplete student (name typed, required field missing) must not
  // slip through: block Continue while any non-blank row still has errors, not just
  // when zero rows are valid. The per-field "Required" errors already show which.
  const hasInvalidRows = validations.some((v) => !v.isBlank && v.errors.length > 0);
  // At least one row has something typed. Gates the Continue button so an
  // untouched form can't be submitted, without disabling it the moment a row is
  // incomplete (we want the click to reveal what's missing instead).
  const hasAnyInput = validations.some((v) => !v.isBlank);

  // Required-field errors stay hidden until the first Continue attempt, so a
  // provider filling a row top-to-bottom doesn't see every field flip to
  // "Required" (and its placeholder darken) the instant they type one letter.
  // After an attempt they update live as fields are fixed.
  const [attemptedContinue, setAttemptedContinue] = useState(false);

  // Parent emails must be unique across the platform, or the family collides
  // with an existing Firebase Auth account. Mirror the single Add Student flow
  // (ParentEditForm): on blur, look the email up platform-wide and flag it if it
  // already belongs to a user in another org. Keyed by row id.
  const [emailInUse, setEmailInUse] = useState<Record<string, string>>({});
  const hasEmailInUse = p.roster.some((r) => emailInUse[r.id]);

  async function checkParentEmail(rowId: string, email: string) {
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setEmailInUse((prev) => (prev[rowId] ? { ...prev, [rowId]: '' } : prev));
      return;
    }
    try {
      const { hasRootUser } = await fetchEmailUsage(trimmed);
      // Orgs with the multi-org switcher may reuse a parent that exists in
      // another org; otherwise a platform-wide match is a hard stop.
      const message = !isMobileOrgSwitcherEnabled && hasRootUser ? t('common.userAlreadyExistsError') : '';
      setEmailInUse((prev) => ({ ...prev, [rowId]: message }));
    } catch {
      // A lookup failure shouldn't block the roster; the save path re-checks.
      setEmailInUse((prev) => (prev[rowId] ? { ...prev, [rowId]: '' } : prev));
    }
  }

  const canContinue = validCount > 0 && !hasInvalidRows && !hasEmailInUse;

  // When the user came from an import source and hasn't imported yet, this page
  // shows the upload zone in place; once a file is imported (or on the manual
  // path), it shows the student cards. No separate "upload" page/navigation.
  const guide = getSourceGuide(p.sourceId);
  const showImport = !!p.sourceId && !p.importedFromFileName;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importingName, setImportingName] = useState<string | null>(null);

  const genderOptions = GENDER_OPTIONS.map((o) => ({ ...o, label: t(o.label) }));
  // Real rooms from the org. Names come from the provider's own data, so they
  // are shown as-is rather than run through t().
  const roomOptions = p.rooms.map((r) => ({ value: r.id, label: r.name }));

  async function handleFile(file: File) {
    setError(null);
    setImportingName(file.name);
    try {
      const text = await file.text();
      const rows = parseCsv(text, p.rooms, p.defaultRoomId);
      if (rows.length === 0) {
        setError(t('addFamilies.roster.importEmptyError'));
        setImportingName(null);
        return;
      }
      // Parsing is instant; hold the spinner briefly so the page resolving into
      // the list reads as an intentional transition rather than an abrupt swap.
      await new Promise((resolve) => setTimeout(resolve, 700));
      p.onImport(rows, file.name);
    } catch (err: any) {
      setError(t('addFamilies.roster.importReadError', { error: err?.message || err }));
      setImportingName(null);
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function withTrailingBlank(rows: RosterRow[]) {
    const last = rows[rows.length - 1];
    return last && !validateRow(last).isBlank ? [...rows, blankRow(p.defaultRoomId)] : rows;
  }

  function update(rowId: string, field: keyof RosterRow, value: string) {
    // A stale "already in use" flag shouldn't linger while the email is edited;
    // it re-checks on blur.
    if (field === 'parentEmail') {
      setEmailInUse((prev) => (prev[rowId] ? { ...prev, [rowId]: '' } : prev));
    }
    const next = p.roster.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
    p.onChange(withTrailingBlank(next));
  }

  function remove(rowId: string) {
    const next = p.roster.filter((r) => r.id !== rowId);
    p.onChange(next.length === 0 ? [blankRow(p.defaultRoomId)] : next);
  }

  function addRow() {
    p.onChange([...p.roster, blankRow(p.defaultRoomId)]);
  }

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="af-step-roster">
      {p.stepper && <div className="border-b border-gray-200 pb-4">{p.stepper}</div>}
      <div>
        <h2 className="text-2xl font-bold text-navy-950">{t('addFamilies.roster.title')}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {!showImport
            ? t('addFamilies.roster.manualDescription')
            : guide
              ? t('addFamilies.roster.sourceUploadDescription', {
                  source: guide.name,
                })
              : t('addFamilies.roster.uploadDescription')}
        </p>
      </div>

      {showImport ? (
        <>
          {guide && <SourceInstructions source={guide} />}
          {importingName ? (
            <div className="flex flex-col items-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-14 text-center">
              <Spinner size={WidgetSizeEnum.LARGE} />
              <div className="mt-4 text-base font-medium text-navy-950">{t('addFamilies.roster.importingTitle')}</div>
              <div className="mt-1 text-xs text-gray-600">
                {t('addFamilies.roster.readingFile', { name: importingName })}
              </div>
            </div>
          ) : (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex cursor-pointer flex-col items-center rounded-lg border-2 border-dashed px-6 py-14 text-center transition ${
                isDragging ? 'border-blue-900 bg-blue-50' : 'border-gray-300 hover:border-blue-900'
              }`}
            >
              <UploadIcon className="h-9 w-9 text-gray-600" />
              <div className="mt-4 text-base font-medium">{t('addFamilies.roster.dropzoneTitle')}</div>
              <div className="mt-2 max-w-md text-xs text-gray-500">{t('addFamilies.roster.dropzoneDescription')}</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={onFileChange}
                className="hidden"
                data-testid="af-csv-file"
              />
              <div className="mt-6">
                <Button
                  primary={false}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  data-testid="af-pick-csv"
                >
                  {t('addFamilies.roster.chooseFileAction')}
                </Button>
              </div>
              {error && <div className="mt-4 text-sm text-red-700">{error}</div>}
            </div>
          )}
        </>
      ) : (
        <>
          {p.importedFromFileName && (
            <div className="rounded-md border border-green-300/50 bg-green-50 px-3 py-2 text-sm text-green-900">
              ✓{' '}
              {t('addFamilies.roster.importSuccess', {
                file: p.importedFromFileName,
              })}
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-3">
              {p.roster.map((row, i) => {
                const v = validations[i];
                const fe = attemptedContinue ? fieldErrors(row) : {};
                const studentName = `${row.childFirstName} ${row.childLastName}`.trim();
                const headerName = studentName || t('addFamilies.roster.studentFallback', { n: i + 1 });

                return (
                  <div
                    key={row.id}
                    className={`rounded-md border border-gray-200 p-4 ${v.isBlank ? 'opacity-70' : ''}`}
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Avatar displayName={headerName} size={WidgetSizeEnum.MEDIUM} />
                        <div>
                          <div className="text-sm font-semibold text-navy-950">{headerName}</div>
                          <div className="text-xs text-gray-600">{t('addFamilies.roster.studentTypeLabel')}</div>
                        </div>
                      </div>
                      <Button
                        size={WidgetSizeEnum.X_SMALL}
                        onClick={() => remove(row.id)}
                        data-testid="af-remove-student"
                        extraClasses="rounded-full ring-0 ring-offset-0 shadow-none text-gray-400 hover:text-red-900"
                      >
                        <DeleteIcon className="size-5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {/* Child: identity + placement + demographics (required first) */}
                      <Input
                        label={t('addFamilies.roster.childFirstNameLabel')}
                        value={row.childFirstName}
                        placeholder={t('addFamilies.roster.firstNamePlaceholder')}
                        size={WidgetSizeEnum.SMALL}
                        error={fe.firstName}
                        onChange={(e) => update(row.id, 'childFirstName', e.target.value)}
                      />
                      <Input
                        label={t('addFamilies.roster.childLastNameLabel')}
                        value={row.childLastName}
                        placeholder={t('addFamilies.roster.lastNamePlaceholder')}
                        size={WidgetSizeEnum.SMALL}
                        error={fe.lastName}
                        onChange={(e) => update(row.id, 'childLastName', e.target.value)}
                      />
                      <Input
                        label={t('addFamilies.roster.childBirthdayLabel')}
                        inputType="date"
                        value={row.childBirthdate}
                        size={WidgetSizeEnum.SMALL}
                        error={fe.childBirthdate}
                        onChange={(e) => update(row.id, 'childBirthdate', e.target.value)}
                      />
                      <Select
                        label={t('addFamilies.roster.roomLabel')}
                        options={roomOptions}
                        size={WidgetSizeEnum.SMALL}
                        placeholder={t('addFamilies.roster.roomLabel')}
                        error={fe.room}
                        value={roomOptions.find((r) => r.value === row.room)}
                        onChange={(opt) => update(row.id, 'room', opt.value)}
                      />
                      <Select
                        label={t('addFamilies.roster.genderLabel')}
                        options={genderOptions}
                        size={WidgetSizeEnum.SMALL}
                        placeholder={t('addFamilies.roster.selectPlaceholder')}
                        value={genderOptions.find((g) => g.value === row.gender)}
                        onChange={(opt) => update(row.id, 'gender', opt.value)}
                      />
                      <Input
                        label={t('addFamilies.roster.allergiesLabel')}
                        value={row.allergies}
                        placeholder={t('addFamilies.roster.allergiesPlaceholder')}
                        size={WidgetSizeEnum.SMALL}
                        onChange={(e) => update(row.id, 'allergies', e.target.value)}
                      />
                      {/* Parent / guardian: contact (required first) */}
                      <Input
                        label={t('addFamilies.roster.parentFirstNameLabel')}
                        value={row.parentFirstName}
                        placeholder={t('addFamilies.roster.firstNamePlaceholder')}
                        size={WidgetSizeEnum.SMALL}
                        error={fe.parentFirstName}
                        onChange={(e) => update(row.id, 'parentFirstName', e.target.value)}
                      />
                      <Input
                        label={t('addFamilies.roster.parentLastNameLabel')}
                        value={row.parentLastName}
                        placeholder={t('addFamilies.roster.lastNamePlaceholder')}
                        size={WidgetSizeEnum.SMALL}
                        error={fe.parentLastName}
                        onChange={(e) => update(row.id, 'parentLastName', e.target.value)}
                      />
                      <EmailInput
                        label={t('addFamilies.roster.parentEmailLabel')}
                        value={row.parentEmail}
                        placeholder="parent@example.com"
                        size={WidgetSizeEnum.SMALL}
                        error={emailInUse[row.id] || fe.email}
                        onChange={(e) => update(row.id, 'parentEmail', e.target.value)}
                        onBlur={() => checkParentEmail(row.id, row.parentEmail)}
                      />
                      <Input
                        label={t('addFamilies.roster.parentPhoneLabel')}
                        value={row.parentPhone}
                        placeholder={t('addFamilies.roster.phonePlaceholder')}
                        size={WidgetSizeEnum.SMALL}
                        onChange={(e) => update(row.id, 'parentPhone', e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <ButtonLink
              content={t('addFamilies.roster.addStudentAction')}
              icon={<AddCircleIcon className="size-4" />}
              onClick={addRow}
            />
          </div>
        </>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <ButtonLink
          content={t('addFamilies.roster.backAction')}
          iconDirection="left"
          icon={<ArrowLeftIcon className="size-4" />}
          onClick={p.onBack}
        />
        {!showImport && (
          <div className="flex flex-col items-end gap-1">
            {attemptedContinue && hasInvalidRows && (
              <p className="text-sm text-red-700">{t('addFamilies.roster.validationSummary')}</p>
            )}
            <Button
              primary
              disabled={!hasAnyInput}
              onClick={() => {
                // First click on an incomplete roster reveals the errors rather
                // than advancing; once everything's valid, it continues.
                if (!canContinue) {
                  setAttemptedContinue(true);
                  return;
                }
                p.onContinue();
              }}
            >
              {t('addFamilies.roster.continueAction')} →
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
